import { useState, useEffect } from "react"

// ─────────────────────────────────────────────────────────────────────────────
// Foto-fetcher: Wikimedia Commons search (niet alleen de Wikipedia-artikelpagina)
// ─────────────────────────────────────────────────────────────────────────────

const BAD_PATTERN = /flag|coat|logo|icon|map|wikimedia|commons-logo|seal|emblem|blank|arms|escudo|bandeira|crest|symbol|diagram|chart|graph|locator|location|relief|topograph|ortho|satellite|aerial|panorama_map|cadastr|plan_/i

function isGoodPhoto(title) {
  if (BAD_PATTERN.test(title)) return false
  if (!/\.(jpg|jpeg|png|webp)$/i.test(title)) return false
  return true
}

async function commonsSearch(query, perQuery = 8) {
  try {
    const url = [
      "https://commons.wikimedia.org/w/api.php",
      "?action=query",
      "&generator=search",
      `&gsrsearch=${encodeURIComponent(query)}`,
      "&gsrnamespace=6",       // File: namespace
      `&gsrlimit=${perQuery}`,
      "&prop=imageinfo",
      "&iiprop=url",
      "&iiurlwidth=1200",
      "&format=json",
      "&origin=*"
    ].join("")

    const res  = await fetch(url)
    const data = await res.json()
    const pages = Object.values(data?.query?.pages || {})

    return pages
      .filter(p => isGoodPhoto(p.title || ""))
      .map(p => p?.imageinfo?.[0]?.thumburl || p?.imageinfo?.[0]?.url)
      .filter(Boolean)
  } catch {
    return []
  }
}

// Fallback: Wikipedia-pagina images (de oude methode)
async function wikiPageImages(wikiPage, limit = 6) {
  if (!wikiPage) return []
  try {
    const listRes = await fetch(
      `https://en.wikipedia.org/w/api.php?action=query&titles=${encodeURIComponent(wikiPage)}&prop=images&imlimit=${limit + 6}&format=json&origin=*`
    )
    const listData = await listRes.json()
    const pages = Object.values(listData?.query?.pages || {})
    if (!pages.length) return []

    const images = (pages[0]?.images || [])
      .map(img => img.title)
      .filter(isGoodPhoto)
      .slice(0, limit)

    if (!images.length) return []

    const urlRes = await fetch(
      `https://en.wikipedia.org/w/api.php?action=query&titles=${encodeURIComponent(images.join("|"))}&prop=imageinfo&iiprop=url&iiurlwidth=1200&format=json&origin=*`
    )
    const urlData = await urlRes.json()
    return Object.values(urlData?.query?.pages || {})
      .map(p => p?.imageinfo?.[0]?.thumburl || p?.imageinfo?.[0]?.url)
      .filter(Boolean)
  } catch {
    return []
  }
}

async function fetchSfeerImages(name, region, concelho, wikiPage, target = 12) {
  // Meerdere query-varianten voor Wikimedia Commons
  const queries = [
    `${name} Portugal`,
    `${name} praia Portugal`,
    `${name} centro Portugal`,
  ]
  if (region && region !== name) {
    queries.push(`${region} Portugal`)
  }
  if (concelho && concelho !== name) {
    queries.push(`${concelho} Portugal`)
  }

  const seen = new Set()
  const results = []

  // Parallel fetchen — sneller
  const batches = await Promise.all(
    queries.map(q => commonsSearch(q, 10))
  )

  for (const batch of batches) {
    for (const url of batch) {
      if (!seen.has(url)) {
        seen.add(url)
        results.push(url)
      }
      if (results.length >= target) break
    }
    if (results.length >= target) break
  }

  // Nog niet genoeg? Vul aan met Wikipedia-pagina
  if (results.length < 5) {
    const fallback = await wikiPageImages(wikiPage, 8)
    for (const url of fallback) {
      if (!seen.has(url)) {
        seen.add(url)
        results.push(url)
      }
      if (results.length >= target) break
    }
  }

  return results
}


// ─────────────────────────────────────────────────────────────────────────────
// Narratief generator
// ─────────────────────────────────────────────────────────────────────────────
function buildNarrative(town, concelho) {
  const cl   = concelho?.climate
  const sea  = town.hardRequirements?.seaDistanceMin
  const sc   = town.scores || {}
  const air  = town.nearestAirport
  const mont = town.nearest_montessori
  const bos  = town.nearest_forest_school
  const vibe = town.vibe || {}

  const regionFeel = {
    "Silver Coast":    "een groene kuststrook tussen Nazaré en Peniche — stiller dan de Algarve, authentieker dan Cascais",
    "Alentejo Coast":  "een uitgestrekte, dunbevolkte kust met gouden graanvelden die recht in de oceaan lopen",
    "Costa Vicentina": "de wildste en mooiste kust van Portugal — nationaal park, nauwelijks bebouwing",
    "Algarve":         "het zonnigste deel van Portugal, met spectaculaire rotskusten en uitstekende voorzieningen",
    "Costa Verde":     "het groene noorden: 's zomers aangenaam, 's winters nat, maar vol leven en cultuur",
    "Lisbon Region":   "op dagelijkse rijafstand van Lissabon — het beste van twee werelden",
  }
  const regionDesc = regionFeel[town.region] || town.region || ""
  const tranq = sc.tranquility_offseason || sc.tranquility || 3
  const drukte = tranq >= 4
    ? "Buiten het zomerseizoen is het rustig en overwegend Portugees."
    : tranq <= 2
    ? "Het is ook in de winter levendig — nooit echt stil."
    : "In het laagseizoen is het dorp aangenaam kalm."
  // Gecureerde, unieke per-town tekst (towns.json) krijgt voorrang op
  // de procedureel gegenereerde zin (die per regio vaak identiek is).
  const curated = (town.blurb || "").trim()
  const lead = curated
    ? curated
    : `${town.name}${regionDesc ? " ligt in " + regionDesc : ""}. ${vibe.tagline ? vibe.tagline + " " : ""}${drukte}`

  const klimParts = []
  if (cl?.annual_sunshine_h) {
    const h = cl.annual_sunshine_h
    klimParts.push(h >= 2900
      ? `Met ${h} zonneuren per jaar schijnt de zon hier bijna altijd.`
      : h >= 2500
      ? `Met ${h} zonneuren haal je de meeste dagen buiten door.`
      : `Met ${h} zonneuren is het aangenaam, met nattere winters.`)
  }
  if (cl?.heat_days_35 != null) {
    const hd = cl.heat_days_35
    klimParts.push(hd <= 8
      ? "Extreme hitte is zeldzaam — prettig voor kinderen buiten."
      : hd <= 20
      ? `Er zijn zo'n ${hd} hete dagen per jaar — de zee is dan dichtbij.`
      : `Zomers zijn echt heet (${hd} dagen boven 35°C) — een zwembad of zee is onmisbaar.`)
  }
  if (sea != null) {
    klimParts.push(sea <= 10
      ? `De zee is ${sea} minuten fietsen — letterlijk de achtertuin.`
      : sea <= 25
      ? `Strand en zee zijn ${sea} minuten rijden, ideaal voor dagelijkse uitstapjes.`
      : `De zee is ${sea} minuten rijden — goed voor een weekendritme.`)
  }
  if (sc.surf >= 4) klimParts.push("Surflessen zijn er in het dorp zelf — geweldig voor avontuurlijke kinderen.")
  if (sc.natureAccess >= 4) klimParts.push("Natuur is overal: stranden, bossen en beschermde gebieden op loopafstand.")

  const schoolParts = []
  if (mont) {
    const km = Math.round(mont.km)
    schoolParts.push(km <= 20
      ? `De Montessori-school in ${mont.school?.name || "de buurt"} is slechts ${km} km — elke ochtend haalbaar.`
      : km <= 50
      ? `De dichtstbijzijnde Montessori is ${km} km — rijden is nodig, maar zeker te doen.`
      : `Montessori-onderwijs is ${km} km weg — een rit die je wekelijks plant.`)
  }
  if (bos && Math.round(bos.km) <= 40) {
    schoolParts.push(`Een bosschool is op ${Math.round(bos.km)} km — goed te combineren.`)
  }
  if (!mont && !bos) {
    schoolParts.push("Alternatieve scholen zijn niet dichtbij — thuisonderwijs of carpooling met andere gezinnen is een optie.")
  }
  if ((sc.youngFamilies || 0) >= 4) schoolParts.push("Er zijn relatief veel jonge gezinnen — jullie kinderen vinden snel vriendjes.")
  else if ((sc.youngFamilies || 0) <= 2) schoolParts.push("De gemeenschap is wat vergrijsd; actief andere gezinnen opzoeken loont.")

  const practParts = []
  const svc = sc.services || 0
  if (svc >= 4) practParts.push("Voorzieningen zijn goed: ziekenhuis, apotheek en supermarkt in de buurt.")
  else if (svc <= 2) practParts.push("Voor ziekenhuis en specialist rij je naar de naburige stad — reken op 20–40 min.")
  if (air) {
    practParts.push(air.driveMin <= 55
      ? `${air.name} is ${air.driveMin} min rijden — bezoek van familie is eenvoudig.`
      : `${air.name} is ${air.driveMin} min rijden — ver genoeg voor rust, bereikbaar voor bezoek.`)
  }
  if (cl?.annual_rainfall_mm) {
    const mm = cl.annual_rainfall_mm
    if (mm <= 500) practParts.push("Het droge klimaat maakt zonnepanelen en regenopvang extra zinvol.")
    else if (mm >= 1100) practParts.push("Het regenseizoen is serieus — goed watermanagement is de moeite waard.")
  }
  if (sc.foodAutonomy >= 4) practParts.push("Markten en boerenboerderijen zijn nabij — lokaal eten is goed te regelen.")

  return {
    lead,
    paras: [klimParts.join(" "), schoolParts.join(" "), practParts.join(" ")].filter(p => p.trim())
  }
}


// ─────────────────────────────────────────────────────────────────────────────
// SfeerLeven component
// ─────────────────────────────────────────────────────────────────────────────
export function SfeerLeven({ town, concelho, wikiPage, heroUrl }) {
  const [photos, setPhotos]     = useState(heroUrl ? [heroUrl] : [])
  const [active, setActive]     = useState(0)
  const [loading, setLoading]   = useState(true)
  const [imgError, setImgError] = useState(false)

  useEffect(() => {
    setActive(0)
    setLoading(true)
    setImgError(false)
    if (heroUrl) setPhotos([heroUrl])

    const name     = town?.name || ""
    const region   = town?.region || ""
    const conc     = town?.concelho || ""

    fetchSfeerImages(name, region, conc, wikiPage, 14).then(imgs => {
      const all = heroUrl
        ? [heroUrl, ...imgs.filter(u => u !== heroUrl)]
        : imgs
      if (all.length) setPhotos(all)
      setLoading(false)
    })
  }, [town?.name, wikiPage, heroUrl])

  const name   = town?.name || ""
  const igTag  = name.toLowerCase().replace(/\s+/g, "").replace(/[^a-z]/g, "")
  const igUrl  = `https://www.instagram.com/explore/tags/${igTag}portugal/`
  const gUrl   = `https://www.google.com/search?q=${encodeURIComponent(name + " Portugal sfeer")}&tbm=isch`
  const wkUrl  = `https://commons.wikimedia.org/w/index.php?search=${encodeURIComponent(name + " Portugal")}&title=Special:MediaSearch&type=image`

  const { lead, paras } = buildNarrative(town, concelho)

  const prev = () => setActive(i => (i - 1 + photos.length) % photos.length)
  const next = () => setActive(i => (i + 1) % photos.length)

  const handleImgError = () => {
    // Sla kapotte foto over
    setPhotos(prev => prev.filter((_, i) => i !== active))
    setActive(i => Math.min(i, photos.length - 2))
  }

  return (
    <section className="sl-section">

      {/* ── Foto-blok ── */}
      <div className="sl-photos">

        {/* Hoofdfoto */}
        <div className="sl-main-wrap">
          {loading && photos.length === 0 ? (
            <div className="sl-loading">
              <span className="sl-loading-spin">⏳</span> Sfeerfotos laden…
            </div>
          ) : photos[active] ? (
            <img
              key={photos[active]}
              src={photos[active]}
              alt={`${name} — sfeer foto ${active + 1}`}
              className="sl-main-img"
              onError={handleImgError}
            />
          ) : (
            <div className="sl-no-photo">Geen foto beschikbaar</div>
          )}

          {photos.length > 1 && (
            <>
              <button className="sl-nav sl-prev" onClick={prev} aria-label="Vorige">‹</button>
              <button className="sl-nav sl-next" onClick={next} aria-label="Volgende">›</button>
              <span className="sl-counter">{active + 1} / {photos.length}</span>
            </>
          )}
        </div>

        {/* Thumbnail-strip */}
        {photos.length > 1 && (
          <div className="sl-thumbs">
            {photos.map((url, i) => (
              <button
                key={url + i}
                className={`sl-thumb${i === active ? " sl-thumb-on" : ""}`}
                onClick={() => setActive(i)}
                aria-label={`Foto ${i + 1}`}
              >
                <img src={url} alt="" loading="lazy" />
              </button>
            ))}
          </div>
        )}

        {/* Social-links */}
        <div className="sl-social">
          <a href={igUrl} target="_blank" rel="noopener" className="sl-social-a">📷 Instagram #{igTag}portugal</a>
          <a href={gUrl}  target="_blank" rel="noopener" className="sl-social-a">🔍 Google Foto's</a>
          <a href={wkUrl} target="_blank" rel="noopener" className="sl-social-a">🌐 Meer op Wikimedia</a>
        </div>
      </div>

      {/* ── Narratief ── */}
      {(lead || paras.length > 0) && (
        <div className="sl-narrative">
          <div className="sl-narr-header">
            <span className="sl-narr-icon">🏡</span>
            <h4 className="sl-narr-title">Over {name}</h4>
            <span className="sl-narr-badge">Auto-gegenereerd</span>
          </div>
          {lead && <p className="sl-narr-lead">{lead}</p>}
          {paras.map((p, i) => (
            <p key={i} className="sl-narr-para">{p}</p>
          ))}
        </div>
      )}

    </section>
  )
}
